'use client'

import Link from 'next/link'
import { formatMinutes, lessonBySlug, lessonCode, neighbors } from '../_lib/curriculum'
import { useProgress } from '../_lib/progress-client'
import { LESSON_COMPONENTS } from '../_lessons/registry'
import GdHeader from './GdHeader'
import { IconArrowLeft, IconArrowRight } from './icons'
import { LessonSlugProvider } from './lesson/Steps'

const KIND_LABEL = { concept: 'Concept', math: 'Math', code: 'Code', model: 'Model', project: 'Project' } as const

export default function LessonView({ slug }: { slug: string }) {
  const lesson = lessonBySlug(slug)!
  const Content = LESSON_COMPONENTS[slug]
  const { prev, next } = neighbors(slug)
  const state = useProgress()
  const moduleDone = lesson.module.lessons.filter(l => state.done[l.slug]).length

  return (
    <>
      <GdHeader
        showProgress
        center={
          <div className="hidden min-w-0 items-center justify-center gap-2 text-[13px] md:flex" style={{ color: 'var(--gd-ink-3)' }}>
            <Link href={`/#module-${lesson.module.id}`} className="truncate hover:underline">
              {lesson.module.n}. {lesson.module.title}
            </Link>
            <span aria-hidden>/</span>
            <span className="truncate" style={{ color: 'var(--gd-ink)' }}>
              {lesson.title}
            </span>
          </div>
        }
      />

      <main id="main-content" className="px-5 pb-24 sm:px-8">
        <div className="gd-prose pb-6 pt-14 sm:pt-20">
          <div className="flex flex-wrap items-center gap-2">
            <span className="gd-chip" style={{ borderColor: 'var(--gd-accent)', color: 'var(--gd-accent)' }}>
              Lesson {lessonCode(lesson)}
            </span>
            {lesson.kinds.map(k => (
              <span key={k} className="gd-chip">
                {KIND_LABEL[k]}
              </span>
            ))}
            <span className="gd-chip" title="A realistic time for a beginner, including the exercises">
              ~{formatMinutes(lesson.minutes)}
            </span>
          </div>
          <h1 className="gd-display mt-6 text-[clamp(44px,7.4vw,76px)]">{lesson.title}</h1>
          <p className="mt-5 text-[20px] leading-[1.55]" style={{ color: 'var(--gd-ink-2)' }}>
            {lesson.summary}
          </p>
          <div className="mt-8 flex items-center gap-3">
            <div className="flex min-w-0 max-w-[340px] flex-1 gap-1" aria-hidden>
              {lesson.module.lessons.map(l => (
                <span
                  key={l.slug}
                  className="block h-[5px] min-w-[5px] max-w-7 flex-1 rounded-full"
                  style={{ background: l.slug === slug ? 'var(--gd-accent)' : state.done[l.slug] ? 'var(--gd-green)' : 'var(--gd-rule-2)' }}
                />
              ))}
            </div>
            <span className="gd-label shrink-0 !text-[10px]" suppressHydrationWarning>
              Module {lesson.module.n} · {moduleDone}/{lesson.module.lessons.length} done
            </span>
          </div>
          <hr className="mt-10 border-0 border-t" style={{ borderColor: 'var(--gd-rule)' }} />
        </div>

        <LessonSlugProvider slug={slug}>{Content ? <Content /> : <ComingSoon />}</LessonSlugProvider>

        <nav className="gd-prose mt-10 grid gap-3 border-t pt-8 sm:grid-cols-2" style={{ borderColor: 'var(--gd-rule)' }} aria-label="Lesson navigation">
          {prev ? (
            <Link href={`/learn/${prev.slug}`} className="gd-card group block p-4 no-underline">
              <span className="gd-label inline-flex items-center gap-1.5">
                <IconArrowLeft size={13} /> Previous · {lessonCode(prev)}
              </span>
              <span className="mt-1 block font-semibold group-hover:text-[var(--gd-accent)]">{prev.title}</span>
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link href={`/learn/${next.slug}`} className="gd-card group block p-4 text-right no-underline">
              <span className="gd-label inline-flex items-center gap-1.5">
                Next · {lessonCode(next)} <IconArrowRight size={13} />
              </span>
              <span className="mt-1 block font-semibold group-hover:text-[var(--gd-accent)]">{next.title}</span>
            </Link>
          )}
        </nav>
      </main>
    </>
  )
}

function ComingSoon() {
  return (
    <div className="gd-prose">
      <p className="gd-p">This lesson is being written. Check back soon.</p>
    </div>
  )
}
